<script setup lang="ts" generic="T, D = never, O extends ISelectOption<T, D> = ISelectOption<T, D>">
import CheckIcon from '@/assets/icons/CheckIcon.vue';
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon.vue';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { SubscriptionManager } from '@/classes/SubscriptionManager';
import { useMobile } from '@/composables/useMobile';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import SearchInput from '@/core/elements/SearchInput.vue';
import { ISelectOption } from '@/interfaces';
import { EMenuAction, getActionFromKey, maintainScrollVisibility } from '@/utils/menu.utils';
import { EPosition } from 'common/enum/misc.enum';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  toRefs,
  useId,
  useTemplateRef,
  watch,
} from 'vue';

const props = withDefaults(
  defineProps<{
    label?: string;
    options: O[];
    modelValue: T;
    theme?: 'inline' | 'input' | 'badge';
    menuPosition?: EPosition;
    disabled?: boolean;
    highlightSelectedOption?: boolean;
    checkSelectedOption?: boolean;
    searchOptions?: {
      placeholder: string;
    };
    separatorIndexes?: number[];
    emitOnInvalidSelection?: boolean;
    emitOnInvalidSelectionValue?: T;
    ariaLabel?: string;
    maxWidth?: number;
    minWidth?: number;
    maxHeight?: number;
    offsetX?: number;
    selectClass?: string;
    alignChevronWithText?: boolean;
    chevronFirst?: boolean;
    menuAppearance?: 'default' | 'popover';
    menuClass?: string;
    menuTitle?: string;
  }>(),
  {
    label: undefined,
    menuPosition: undefined,
    highlightSelectedOption: false,
    checkSelectedOption: false,
    theme: 'inline',
    searchOptions: undefined,
    separatorIndexes: () => [],
    emitOnInvalidSelection: false,
    emitOnInvalidSelectionValue: undefined,
    ariaLabel: undefined,
    maxWidth: undefined,
    minWidth: undefined,
    maxHeight: undefined,
    offsetX: -10,
    selectClass: undefined,
    alignChevronWithText: false,
    chevronFirst: false,
    menuAppearance: 'default',
    menuClass: undefined,
    menuTitle: undefined,
  },
);
const { modelValue, options, disabled, searchOptions, maxHeight } = toRefs(props);

interface IEmits {
  (event: 'update:modelValue', data?: T): void;
  (event: 'locked-option-click', data: T): void;
}
const emit = defineEmits<IEmits>();

defineSlots<{
  option(props: { option: O; isActive: boolean; isSelected: boolean }): void;
  selected(props: { option: O | null }): void;
  selectedIcon(props: { option: O | null }): void;
  endItem(): void;
  emptyOptions(): void;
}>();

const name = ref<string>(useId());
const menuOpen = ref<boolean>(false);
const ignoreBlur = ref<boolean>(false);
const activeOptionId = ref<string>('');
const activeOptionValue = ref<T | null>(null);
const selectedOption = shallowRef<O | null>(null);
const searchQuery = ref<string>('');

const subscriptionManager = new SubscriptionManager();
const iconWidth = ref<number>(0);

const activeOptionRef = ref<HTMLElement | null>(null);
const inputRef = useTemplateRef('inputRef');
const menuRef = useTemplateRef('menuRef');
const menuDialogRef = useTemplateRef('menuDialogRef');
const selectedIconRef = useTemplateRef('selectedIconRef');

const { isMobile } = useMobile();
const internalMaxHeight = computed<number>(() => {
  if (maxHeight.value) return maxHeight.value;
  if (isMobile.value) return 400;
  return 600;
});

const updateIconWidth = (): void => {
  if (!selectedIconRef.value) return;
  const rect = selectedIconRef.value.getBoundingClientRect();
  iconWidth.value = rect.width;
};

const updateSubscriptions = (): void => {
  subscriptionManager.disposeSubscriptions();
  if (!selectedIconRef.value) return;
  subscriptionManager.subscribeToElementResize(selectedIconRef.value, updateIconWidth);
  void nextTick(updateIconWidth);
};

onMounted((): void => {
  updateMenuScroll();
  updateSubscriptions();
});

onBeforeUnmount(() => {
  subscriptionManager.disposeSubscriptions();
});

const getUpdatedOptionValue = (current: T, action: EMenuAction): T => {
  const currentIndex = options.value.findIndex((o: O) => o.value === current);
  switch (action) {
    case EMenuAction.First:
      return options.value[0].value;
    case EMenuAction.Last:
      return options.value[options.value.length - 1].value;
    case EMenuAction.Previous:
      return options.value[currentIndex - 1]
        ? options.value[currentIndex - 1].value
        : options.value[currentIndex].value;
    case EMenuAction.Next:
      return options.value[currentIndex + 1]
        ? options.value[currentIndex + 1].value
        : options.value[currentIndex].value;
    default:
      return current;
  }
};

const filteredOptions = computed<O[]>(() => {
  if (!searchOptions.value || !searchQuery.value.trim()) return options.value;
  const query = searchQuery.value.toLowerCase();
  return options.value.filter((option: O) => option.label.toLowerCase().includes(query));
});

const handleMenuKeydown = (event: KeyboardEvent): void => {
  const action = getActionFromKey(event, menuOpen.value);
  switch (action) {
    case EMenuAction.Next:
    case EMenuAction.Last:
    case EMenuAction.First:
    case EMenuAction.Previous:
      event.preventDefault();
      return onOptionChange(getUpdatedOptionValue(activeOptionValue.value, action));
    case EMenuAction.CloseSelect:
    case EMenuAction.Space:
      event.preventDefault();
      return selectOption(activeOptionValue.value);
    case EMenuAction.Close:
      event.preventDefault();
      return updateMenuState(false);
    case EMenuAction.Open:
      event.preventDefault();
      return updateMenuState(true);
  }
};

const handleSelectKeydown = (event: KeyboardEvent): void => {
  if (menuOpen.value) {
    handleMenuKeydown(event);
    return;
  }

  const action = getActionFromKey(event, false);
  if (action === EMenuAction.Open) {
    event.preventDefault();
    updateMenuState(true);
  }
};

const handleSelectClick = (): void => {
  updateMenuState(!menuOpen.value);
};

const onOptionChange = (val: T): void => {
  activeOptionValue.value = val;
};

const selectOption = (val: T): void => {
  const selected = options.value.find((o: O) => o.value === val) ?? null;
  if (selected?.selectable === false) return;
  selectedOption.value = selected;
  emit('update:modelValue', selected.value);
  updateMenuState(false);
};

const updateMenuState = (openState: boolean, callFocus = true): void => {
  menuOpen.value = openState;
  if (openState) {
    menuDialogRef.value?.show();
  } else {
    activeOptionRef.value = null;
    menuDialogRef.value?.hide();
  }
  if (callFocus) inputRef.value?.focus();
  updateMenuScroll();
};

const updateMenuScroll = (): void => {
  if (activeOptionRef.value && menuRef.value && menuOpen.value) {
    maintainScrollVisibility(activeOptionRef.value, menuRef.value);
    void nextTick(() => {
      activeOptionRef.value?.scrollIntoView(false);
    });
  }
};

const handleOptionClick = (val: T): void => {
  const option = options.value.find((o: O) => o.value === val);
  if (option?.selectable === false) {
    emit('locked-option-click', val);
    updateMenuState(false);
    return;
  }
  onOptionChange(val);
  selectOption(val);
  updateMenuState(false);
};

const handleOptionMousedown = (): void => {
  ignoreBlur.value = true;
};

const handleOptionKeydown = (): void => {
  ignoreBlur.value = true;
};

const refActiveOption = (el: unknown, val: T): void => {
  if (val === activeOptionValue.value) {
    activeOptionRef.value = el as HTMLElement;
  }
};

const hasOptions = computed<boolean>(() => {
  return props.options.length > 0;
});

watch(
  [modelValue, options],
  () => {
    const selected = options.value.find((o: O) => o.value === modelValue.value) ?? null;
    if (props.emitOnInvalidSelection && modelValue.value !== selected?.value) {
      emit('update:modelValue', props.emitOnInvalidSelectionValue);
    }
    selectedOption.value = selected ?? null;
    activeOptionValue.value = selected?.value ?? null;
  },
  { immediate: true },
);

watch(
  [menuOpen, activeOptionValue, filteredOptions],
  () => {
    const activeIndex = filteredOptions.value.findIndex((option) => option.value === activeOptionValue.value);
    activeOptionId.value =
      menuOpen.value && activeIndex >= 0 ? `inline-select-option-${name.value}-${activeIndex}` : '';
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="relative">
      <label
        v-if="label"
        :id="`inline-select-label-${name}`"
        :for="`inline-select-input-${name}`"
        :class="['input-text-label', selectedOption && 'label-floating']"
        :style="{ paddingLeft: $slots.selectedIcon && `${iconWidth + 13}px` }"
      >
        {{ label }}
      </label>
      <div
        :id="`inline-select-input-${name}`"
        ref="inputRef"
        role="combobox"
        :aria-activedescendant="activeOptionId"
        :aria-label="ariaLabel"
        aria-autocomplete="none"
        aria-haspopup="listbox"
        :aria-expanded="menuOpen"
        :aria-labelledby="label ? `inline-select-label-${name}` : undefined"
        :aria-controls="`inline-select-menu-${name}`"
        :class="[
          'flex items-center cursor-pointer',
          alignChevronWithText ? 'justify-center gap-xs' : chevronFirst ? 'justify-start gap-xs' : 'justify-between',
          'outline-inner state-effects',
          menuOpen && 'outline-2 outline-tint outline-offset-[-2px]',
          disabled && 'input-text-disabled',
          theme === 'input' ? 'input-text bg-white' : 'rounded-md px-md py-xs',
          theme === 'inline' && 'border-0',
          theme === 'badge' && 'rounded-badge pr-sm min-h-auto gap-xs font-normal',
          highlightSelectedOption && '!bg-surface-green text-on-tint',
          label && theme === 'input' && 'input-text-labeled',
          selectClass,
        ]"
        :style="{ paddingLeft: $slots.selectedIcon && `${iconWidth + 16}px` }"
        tabindex="0"
        @click.stop="handleSelectClick"
        @keydown="handleSelectKeydown"
      >
        <div ref="selectedIconRef" class="absolute left-[10px] top-1/2 -translate-y-1/2">
          <slot name="selectedIcon" :option="selectedOption" />
        </div>
        <div
          :class="[
            'min-w-0 truncate text-body',
            chevronFirst ? 'order-2' : 'order-1',
            alignChevronWithText ? 'max-w-full' : 'flex-grow',
          ]"
        >
          <slot name="selected" :option="selectedOption">
            {{ selectedOption?.label }}
          </slot>
        </div>
        <div
          :class="[
            chevronFirst ? 'order-1' : 'order-2',
            alignChevronWithText || chevronFirst ? 'flex size-icon-sm shrink-0 items-center justify-center' : 'w-icon',
          ]"
        >
          <ChevronDownIcon
            :class="[
              'size-icon-sm shrink-0',
              !alignChevronWithText && !chevronFirst && 'absolute right-[13px] top-1/2 -translate-y-1/2',
              theme === 'badge' && 'size-icon-xs',
              highlightSelectedOption ? 'text-on-tint' : 'text-secondary',
            ]"
          />
        </div>
      </div>
      <PositionablePopover
        ref="menuDialogRef"
        scroll-mode="content"
        :anchor-element="inputRef"
        content-style="overflow-x-hidden"
        :offset-x="offsetX"
        :full-width="menuPosition == null"
        flip-strategy="vertical"
        :position="menuPosition"
        @hide="menuOpen = false"
      >
        <div class="flex min-h-0 flex-col rounded-md">
          <ul
            :id="`inline-select-menu-${name}`"
            ref="menuRef"
            :class="[
              'w-full min-h-0 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain rounded-md bg-surface-primary',
              menuAppearance === 'popover'
                ? '!px-smd !py-smd'
                : searchOptions && hasOptions
                  ? 'px-md pt-md pb-sm'
                  : 'p-sm',
              menuClass,
            ]"
            :style="{
              maxHeight: `${internalMaxHeight}px`,
              maxWidth: maxWidth ? `${maxWidth}px` : undefined,
              minWidth: minWidth ? `${minWidth}px` : undefined,
            }"
            role="listbox"
            @keydown="handleMenuKeydown"
          >
            <SearchInput
              v-if="searchOptions && hasOptions"
              :model-value="searchQuery"
              :label="$t('general.search')"
              :placeholder="searchOptions.placeholder"
              @update:model-value="searchQuery = $event"
            />
            <li v-if="menuTitle" role="presentation" class="px-sm pb-xs text-md font-semibold text-primary">
              {{ menuTitle }}
            </li>
            <template v-if="!hasOptions">
              <li>
                <slot name="emptyOptions"></slot>
              </li>
            </template>
            <template v-for="(option, index) in filteredOptions" :key="`input-select-${name}-option-${index}`">
              <li
                :id="`inline-select-option-${name}-${index}`"
                :ref="(el) => refActiveOption(el, option.value)"
                tabindex="0"
                :class="[
                  menuAppearance === 'popover' ? 'popover-option' : 'dropdown-option',
                  activeOptionValue === option.value &&
                    (menuAppearance === 'popover' ? '!bg-highlight' : !checkSelectedOption && 'bg-surface-tertiary'),
                ]"
                :aria-selected="modelValue === option.value ? 'true' : false"
                role="option"
                @click="handleOptionClick(option.value)"
                @mousedown="handleOptionMousedown"
                @keydown="handleOptionKeydown"
              >
                <slot
                  name="option"
                  :option="option"
                  :is-active="activeOptionValue === option.value"
                  :is-selected="modelValue === option.value"
                >
                  <span class="mr-auto min-w-0 flex-1 whitespace-normal break-words text-left font-normal">
                    {{ option.label }}
                  </span>
                </slot>
                <component
                  :is="modelValue === option.value ? (menuAppearance === 'popover' ? CheckIcon : CheckMarkIcon) : 'div'"
                  v-if="checkSelectedOption"
                  :class="['size-icon shrink-0 text-tint', menuAppearance === 'default' && 'ml-md']"
                />
              </li>
              <CustomSeparator
                v-if="separatorIndexes.includes(index)"
                :class="menuAppearance === 'popover' ? 'mx-sm my-sm' : 'mx-2 my-2'"
              />
            </template>
            <li v-if="$slots.endItem">
              <CustomSeparator class="mx-2" />
              <div class="flex items-stretch">
                <slot name="endItem"></slot>
              </div>
            </li>
          </ul>
        </div>
      </PositionablePopover>
    </div>
  </div>
</template>
